import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { registryRecordHref } from '../../../components/SharedRecord';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { dateInput, vehicleSaveError } from './contractVehicleUi';
import { relationshipLabel, VehicleRelationshipEditor } from './VehicleRelationshipEditor';
import { useVehicleCompanies, useVehicleProfile, useVehicleRelationships, useVehicleWrites } from './vehicleIdentityHooks';
function RelationshipVehicle({ id, origin }) {
    const profile = useVehicleProfile(id);
    return /*#__PURE__*/ _jsx(Link, {
        className: "text-primary underline",
        to: registryRecordHref('vehicle', id, origin),
        children: profile.data?.vehicle?.name || `Vehicle · ${id}`
    });
}
export function VehicleRelationshipsSection({ vehicleId = '', companyId = '' }) {
    const [history, setHistory] = useState(false);
    const [editor, setEditor] = useState(null);
    const [archiving, setArchiving] = useState('');
    const query = useVehicleRelationships(vehicleId, companyId, history);
    const companies = useVehicleCompanies();
    const writes = useVehicleWrites();
    const canEdit = useTenantAreas().registries;
    const location = useLocation();
    const origin = location.pathname + location.search;
    const label = (id)=>companies.data?.find((c)=>c.id === id)?.name || `Company · ${id || 'unknown'}`;
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        "data-testid": Testids.vehicleIdentity.relationships,
        "aria-label": "Recorded holdings / participation",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Recorded holdings / participation. No holdings recorded is not a verified zero. Relationships apply only to the named company and actual holder, scope and period. No parent or JV member inherits these facts."
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Relationship history",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Relationship history",
                    value: history ? 'all' : 'current',
                    onChange: (e)=>setHistory(e.target.value === 'all'),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "current",
                            children: "Current records"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "all",
                            children: "Include archived history"
                        })
                    ]
                })
            }),
            canEdit && !editor && /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                onClick: ()=>setEditor('new'),
                children: "Record relationship"
            }),
            editor && canEdit && /*#__PURE__*/ _jsx(VehicleRelationshipEditor, {
                relationship: editor === 'new' ? undefined : editor,
                vehicleId: vehicleId,
                companyId: companyId,
                onDone: ()=>setEditor(null)
            }, editor === 'new' ? 'new' : editor.id),
            query.isPending ? /*#__PURE__*/ _jsx("output", {
                children: "Loading relationships…"
            }) : query.isError ? /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Could not load relationships.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void query.refetch(),
                        children: "Retry relationships"
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            query.data.length,
                            " visible loaded relationships"
                        ]
                    }),
                    query.data.length === 0 && /*#__PURE__*/ _jsx("p", {
                        children: "No holdings recorded. A holding may exist without being recorded or visible here."
                    }),
                    query.data.map((r)=>/*#__PURE__*/ _jsxs("article", {
                            className: "space-y-2 rounded border p-3",
                            "data-testid": Testids.vehicleIdentity.relationshipRow,
                            "data-record-id": r.id,
                            children: [
                                !vehicleId && /*#__PURE__*/ _jsx(RelationshipVehicle, {
                                    id: r.vehicleId,
                                    origin: origin
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        /*#__PURE__*/ _jsx(Link, {
                                            className: "text-primary underline",
                                            to: registryRecordHref('company', r.companyId, origin),
                                            children: label(r.companyId)
                                        }),
                                        ' ',
                                        "· ",
                                        relationshipLabel(r.relationshipType),
                                        r.archivedAt ? ' · Archived' : ''
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "Actual holder / intermediary:",
                                        ' ',
                                        r.holderCompanyId ? /*#__PURE__*/ _jsx(Link, {
                                            className: "text-primary underline",
                                            to: registryRecordHref('company', r.holderCompanyId, origin),
                                            children: label(r.holderCompanyId)
                                        }) : 'Unknown'
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "Holder PIID: ",
                                        r.holderContractNumber || 'Unknown',
                                        ' ',
                                        r.holderRegistryContractId && /*#__PURE__*/ _jsx(Link, {
                                            className: "text-primary underline",
                                            to: `/registries?tab=contracts&record=${encodeURIComponent(r.holderRegistryContractId)}&returnTo=${encodeURIComponent(origin)}`,
                                            children: "Open contextual holder contract"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: r.scope ? `Scope: ${r.scope}` : 'Scope unknown — not all pools / domains / SINs'
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "Holding period: ",
                                        dateInput(r.holdingStart) || 'Unknown',
                                        " –",
                                        ' ',
                                        dateInput(r.holdingEnd) || 'Unknown'
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    children: r.sourceBasis || 'Source / basis unknown'
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "Source verification: ",
                                        r.verificationStatus || 'UNVERIFIED',
                                        r.verifiedFact ? ` · ${r.verifiedFact}` : ' · Fact not recorded'
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    children: [
                                        "Verification period: ",
                                        dateInput(r.verificationStart) || 'Unknown',
                                        " –",
                                        ' ',
                                        dateInput(r.verificationEnd) || 'Unknown',
                                        ". This is not procurement eligibility."
                                    ]
                                }),
                                r.notes && /*#__PURE__*/ _jsx("p", {
                                    children: r.notes
                                }),
                                canEdit && !r.archivedAt && /*#__PURE__*/ _jsxs("div", {
                                    className: "flex gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "secondary",
                                            onClick: ()=>setEditor(r),
                                            children: "Edit relationship"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "secondary",
                                            "data-testid": Testids.vehicleIdentity.archiveRelationship,
                                            onClick: ()=>setArchiving(r.id),
                                            children: "Archive relationship"
                                        })
                                    ]
                                }),
                                archiving === r.id && /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            children: "Archive this recorded relationship? Its history and references are retained."
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            disabled: writes.archive.isPending,
                                            onClick: async ()=>{
                                                try {
                                                    await writes.archive.mutateAsync(r);
                                                    setArchiving('');
                                                } catch  {
                                                /* keep confirmation and error */ }
                                            },
                                            children: "Confirm archive"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "secondary",
                                            onClick: ()=>setArchiving(''),
                                            children: "Cancel archive"
                                        })
                                    ]
                                })
                            ]
                        }, r.id))
                ]
            }),
            writes.archive.error && /*#__PURE__*/ _jsxs("p", {
                role: "alert",
                children: [
                    vehicleSaveError(writes.archive.error),
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void query.refetch(),
                        children: "Reload relationships"
                    })
                ]
            })
        ]
    });
}
